import { GIFEncoder, quantize, applyPalette } from 'gifenc';

// Draws the copied email signature as pictures. The page's preview is HTML;
// this redraws the same card on a canvas — the light background with its
// moving lines, the logo and NANOFIBRE.CO.UK, and the person's details — and
// cuts it into slices, each an animated GIF. The copied signature is those
// images, because Outlook strips background images and recolours HTML text
// (dark mode, pasted styles) but never touches pictures. Layout and sizes
// follow the HTML in EmailSignatures.jsx.

const PURPLE = '#711C91';
const INK = '#1f1027';
const TEXT = '#2B2B2B';
const FONT = "'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Helvetica, Arial, sans-serif";
const FRAMES = 48;
const FRAME_MS = 95; // ~4.6s loop, as the page's background GIFs
const SCALE = 2; // drawn at 2x for sharp screens

// Deterministic pseudo-random, so the line field is the same every time (and
// the same as the page's background GIFs, scripts/signature-assets.js).
function rng(seed) {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

// A few lines rising gently left to right across the lower part of the card.
function lineField(W, H, count, seed) {
  const r = rng(seed);
  const lines = [];
  for (let k = 0; k < count; k++) {
    const y0 = 0.55 * H + (k + r() * 0.5) * ((0.75 * H) / count);
    const rise = (0.45 + r() * 0.25) * H;
    const amp = (0.04 + r() * 0.06) * H * (r() < 0.5 ? -1 : 1);
    const y1 = y0 - rise;
    const d = `M-30 ${y0.toFixed(1)} C ${(W * 0.3).toFixed(1)} ${(y0 - rise * 0.25 + amp).toFixed(1)}, ${(W * 0.65).toFixed(1)} ${(y1 + rise * 0.3 - amp).toFixed(1)}, ${W + 30} ${y1.toFixed(1)}`;
    lines.push({ d, w: 0.8 + r() * 0.3, o: 0.1 + r() * 0.1 });
  }
  return lines;
}

const SOCIAL_R = 10; // social buttons: discs, 20px across
const SOCIAL_STEP = 24; // their centres apart (L.socials.xs)
// The banner under the card, drawn into the same picture: its height with the
// certifications or as just the strip (scripts/signature-certs.js makes both,
// without the logos), and the carousel of certification logos sliding on it.
const BANNER_H = { certs: 102, strip: 30 };
const CAROUSEL_TOP = 22; // from the banner's top
const CAROUSEL_H = 50;
const CERT_LOGOS = ['iso27001', 'safecontractor', 'smartawards', 'shift'];
const HOLD_FRAMES = 21; // ~2s on each logo
const SLIDE_FRAMES = 3;

// The company a signature is for: its colours, logo and site. layouts
// overrides SIGNATURE_LAYOUTS (the Utilities logo is long and thin). Nano
// Utilities' card is their main navy, with the white logo made for dark
// backgrounds; lime is their accent. halo: the glow around text and logos,
// the card's own colour so the streaks fade out as they reach them. accentInk:
// the symbols on accent-coloured discs.
export const SIGNATURE_BRANDS = {
  fibre: {
    label: 'Nano Fibre',
    name: 'Nano Fibre UK',
    onePicture: true, // see sliceSignature
    banners: ['certs-still', 'nu-solo-still'],
    website: 'https://nanofibre.co.uk',
    // At L.socials.xs, in order: the company's own site, then its pages.
    buttons: [
      { key: 'website', label: 'Nano Fibre website', mark: 'fibre', href: 'https://nanofibre.co.uk' },
      { key: 'linkedin', label: 'LinkedIn', glyph: 'in', href: 'https://www.linkedin.com/company/nano-fibre-uk-limited' },
      { key: 'facebook', label: 'Facebook', glyph: 'f', href: 'https://www.facebook.com/gigaspeedbroadband' },
    ],
    site: 'NANOFIBRE.CO.UK',
    siteSpacing: 2,
    logo: 'logo.png',
    ink: INK,
    text: TEXT,
    accent: PURPLE,
    accentInk: '#ffffff',
    halo: '#ffffff',
    rule: PURPLE,
    streak: PURPLE,
    paper: ['#ffffff', '#fbf8fd', '#f2eaf5'],
    glows: ['#D8C1E1', '#E5D6EB'],
    dividerRgb: '113,28,145',
    layouts: {},
  },
  utilities: {
    label: 'Nano Utilities',
    name: 'Nano Utilities',
    seam: '#0B2340', // behind the pictures: see buildImageHtml
    banners: ['nu-certs-still', 'nu-part-of-still'],
    website: 'https://nanoutilities.co.uk',
    buttons: [
      { key: 'website', label: 'Nano Utilities website', mark: 'utilities', href: 'https://nanoutilities.co.uk' },
      { key: 'linkedin', label: 'LinkedIn', glyph: 'in', href: 'https://www.linkedin.com/company/nano-utilities/' },
      { key: 'facebook', label: 'Facebook', glyph: 'f', href: 'https://www.facebook.com/nanoutilities' },
    ],
    site: 'NANOUTILITIES.CO.UK',
    siteSpacing: 1.2,
    logo: 'nano-utilities-logo-white.svg',
    ink: '#ffffff',
    text: '#ffffff',
    accent: '#D9E021',
    accentInk: '#0B2340',
    halo: '#0B2340',
    rule: '#D9E021',
    streak: '#D9E021',
    tintIcons: true,
    paper: ['#0B2340', '#0B2340', '#102C4C'],
    glows: ['#163556', '#163556'],
    dividerRgb: '200,210,222',
    layouts: {
      laptop: { logo: [16, 17, 158, 28] },
      phone: { logo: [18, 6, 126, 22], site: { x: 18, y: 38, align: 'left' }, socials: { y: 24, xs: [222, 246, 270] } },
    },
  },
};

// Laptop: brand column on the left — the logo, NANOFIBRE.CO.UK and the
// LinkedIn and Facebook buttons — with the details beside it. Phone: details
// on top, the same brand pieces as the footer (logo left; site right with the
// buttons under it). The card's height fits its contents (layoutSignature).
// Incorporating Nano Utilities goes under the certifications banner (the
// page's belowCard), not on the card.
// Brand positions are from the column (laptop) or footer (phone) top.
export const SIGNATURE_LAYOUTS = {
  laptop: {
    W: 816,
    carousel: { slots: 4, tile: [170, 40] },
    lineCount: 6,
    seed: 7,
    streaks: [[2, 0.1], [4, 0.6]],
    pad: 6, // above and below the name / contacts, at least
    rowH: 21, // one contact line: 18px icon with 1.5px above and below
    brandH: 104, // the brand column's height
    logo: [45, 12, 100, 37],
    site: { x: 95, y: 61, align: 'center' },
    socials: { y: 83, xs: [71, 95, 119] }, // disc centres
    divider: 190, // a faint purple line between the brand column and the name
    rule: [4, 4], // gaps above and below the 2px purple rule under the title
    detailsX: 236, // name, title and contact lines
    sizes: { name: 20, title: 11, text: 13, icon: 18 },
  },
  phone: {
    W: 300,
    carousel: { slots: 2, tile: [130, 40] },
    lineCount: 5,
    seed: 11,
    streaks: [[2, 0.3]],
    detailsX: 18,
    detailsTop: 12,
    rowH: 22, // one contact line: 18px icon with 2px above and below
    rule: [5, 4], // gaps above and below the 2px purple rule under the title
    footerGap: 8, // between the last line and the footer
    footerH: 48,
    logo: [18, 4, 84, 31],
    site: { x: 282, y: 12, align: 'right' },
    socials: { y: 32, xs: [222, 246, 270] },
    sizes: { name: 18, title: 10, text: 13, icon: 18 },
  },
};

let measureCtx = null;
// The font size (at most `size`) at which bold `text` fits in `maxWidth`, so
// a long name or email shrinks a little instead of running past the card.
// Shared with the page's HTML preview, so both shrink alike.
export function fitTextSize(text, size, maxWidth, letterSpacing = 0) {
  if (!text) return size;
  measureCtx = measureCtx || document.createElement('canvas').getContext('2d');
  let s = size;
  while (s > 9) {
    measureCtx.font = `bold ${s}px ${FONT}`;
    if ('letterSpacing' in measureCtx) measureCtx.letterSpacing = `${letterSpacing}px`;
    if (measureCtx.measureText(text).width <= maxWidth) break;
    s -= 0.5;
  }
  return s;
}

// Width the details text can use (1x): its column, less a margin; contact
// lines also lose the icon and its gap.
export function detailsWidth(layoutKey, isRow = false) {
  const L = SIGNATURE_LAYOUTS[layoutKey];
  return L.W - L.detailsX - 18 - (isRow ? L.sizes.icon + 10 : 0);
}

// Where everything goes for these details (card coordinates, 1x). The card
// is as tall as its contents: name, title, rule and contact lines — laptop
// beside the brand column, the two centred top to bottom, the card as tall
// as the taller (with `pad` above and below); phone, then the footer.
export function layoutSignature(content, layoutKey, brandKey = 'fibre', certs = false) {
  const brand = SIGNATURE_BRANDS[brandKey];
  const L = { ...SIGNATURE_LAYOUTS[layoutKey], ...brand.layouts[layoutKey], brand, brandKey, layoutKey };
  const { name, title, rows } = content;
  const textW = detailsWidth(layoutKey);
  const rowW = detailsWidth(layoutKey, true);
  const nameH = L.sizes.name + 5;
  const titleH = title ? L.sizes.title + 5 + 3 : 0;
  const ruleH = L.rule[0] + 2 + L.rule[1];
  const rowsH = rows.length * L.rowH;
  let H;
  let top;
  let brandY;
  const detailsH = nameH + titleH + ruleH + rowsH;
  if (L.divider) {
    H = Math.max(L.brandH, detailsH + 2 * L.pad);
    top = Math.round((H - detailsH) / 2);
    brandY = Math.round((H - L.brandH) / 2);
  } else {
    top = L.detailsTop;
    brandY = top + detailsH + L.footerGap;
    H = brandY + L.footerH;
  }
  const rowsTop = top + nameH + titleH + ruleH;
  const cardH = H;
  const banner = certs ? 'certs' : 'strip';
  H += BANNER_H[banner];
  return {
    L,
    H,
    cardH,
    banner,
    brandY,
    lines: lineField(L.W, cardH, L.lineCount, L.seed),
    name,
    title,
    nameSize: fitTextSize(name, L.sizes.name, textW, -0.2),
    titleSize: fitTextSize(title.toUpperCase(), L.sizes.title, textW, 1.4),
    nameY: top + nameH / 2,
    titleY: top + nameH + 3 + (L.sizes.title + 5) / 2,
    ruleY: top + nameH + titleH + L.rule[0],
    rows: rows.map((row, i) => ({ ...row, y: rowsTop + i * L.rowH, size: fitTextSize(row.text, L.sizes.text, rowW) })),
  };
}

// The copied card is cut into as few pictures as still lets each button have
// its link: Outlook lays out and scales each pasted picture on its own, so
// wherever two meet it can show a hairline or shift one by a pixel. Three
// bands, the middle one holding the buttons as their own linked pictures,
// side by side (each as wide as the step between them, so there are no gaps
// to fill); everything else links to the website. The banner is drawn into
// the same picture, because a join between card and banner shows too.
//
// Those joins show as lines: pale on Nano Utilities' navy in light mode, which
// the colour behind its pictures covers (buildImageHtml), and dark on Nano
// Fibre's pale card in dark mode, which no colour would cover (dark mode
// darkens it). So with onePicture the card isn't cut: its buttons are only
// drawn on, and it all links to the website.
export function sliceSignature(lay) {
  const { L, H } = lay;
  const { name, website, site, buttons, onePicture } = L.brand;
  const details = [lay.name, lay.title, ...lay.rows.map((r) => r.text)].filter(Boolean).join(', ');
  const alt = `${details} — ${name}, ${site.toLowerCase()}`;
  if (onePicture) return [{ key: 'card', x: 0, y: 0, w: L.W, h: H, href: website, alt, group: 'b1' }];

  const y = lay.brandY + L.socials.y - SOCIAL_R;
  const h = 2 * SOCIAL_R;
  const left = L.socials.xs[0] - SOCIAL_STEP / 2;
  const right = left + buttons.length * SOCIAL_STEP;
  return [
    { key: 'top', x: 0, y: 0, w: L.W, h: y, href: website, alt, group: 'b1' },
    { key: 'left', x: 0, y, w: left, h, href: website, alt: '', group: 'b2' },
    ...buttons.map((b, i) => ({ key: b.key, x: left + i * SOCIAL_STEP, y, w: SOCIAL_STEP, h, href: b.href, alt: b.label, group: 'b2' })),
    { key: 'rest', x: right, y, w: L.W - right, h, href: website, alt: '', group: 'b2' },
    { key: 'bottom', x: 0, y: y + h, w: L.W, h: H - y - h, href: website, alt: '', group: 'b3' },
  ];
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Couldn't load ${src}`));
    img.src = src;
  });
}

function makeCanvas(w, h) {
  const c = document.createElement('canvas');
  c.width = Math.round(w);
  c.height = Math.round(h);
  return c;
}

// A contact icon (purple disc, white symbol) in other colours. The icon's
// green channel runs from the purple's 28 to white's 255, which says how much
// of each pixel is symbol.
function tinted(img, disc, symbol) {
  const c = makeCanvas(img.naturalWidth, img.naturalHeight);
  const ctx = c.getContext('2d');
  ctx.drawImage(img, 0, 0);
  const rgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  const [from, to] = [rgb(disc), rgb(symbol)];
  const pixels = ctx.getImageData(0, 0, c.width, c.height);
  const { data } = pixels;
  for (let o = 0; o < data.length; o += 4) {
    const white = Math.max(0, (data[o + 1] - 28) / (255 - 28));
    for (let k = 0; k < 3; k++) data[o + k] = from[k] + (to[k] - from[k]) * white;
  }
  ctx.putImageData(pixels, 0, 0);
  return c;
}

// Images the drawing needs (served from public/signature), per brand.
export async function loadSignatureAssets(assetsBase) {
  const load = (f) => loadImage(`${assetsBase}/${f}`);
  const [phone, mobile, email, fibreLogo] = await Promise.all(['icon-phone.png', 'icon-mobile.png', 'icon-email.png', 'logo.png'].map(load));
  const entries = await Promise.all(
    Object.entries(SIGNATURE_BRANDS).map(async ([key, brand]) => {
      const icon = (img) => (brand.tintIcons ? tinted(img, brand.accent, brand.accentInk) : img);
      const banners = { certs: {}, strip: {} };
      const tiles = {};
      for (const [layout, { W }] of Object.entries(SIGNATURE_LAYOUTS)) {
        banners.certs[layout] = await load(`${brand.banners[0]}-${W}.png`);
        banners.strip[layout] = await load(`${brand.banners[1]}-${W}.png`);
        tiles[layout] = await Promise.all(CERT_LOGOS.map((name) => load(`cert-tile-${name}-${W}.png`)));
      }
      return [key, { logo: await load(brand.logo), fibreLogo, banners, tiles, icons: { phone: icon(phone), mobile: icon(mobile), email: icon(email) } }];
    })
  );
  await document.fonts?.ready;
  return Object.fromEntries(entries);
}

// A glow shaped like a logo, a little larger than it, so the lines fade out
// around its letters.
function logoGlow(img, w, h, colour) {
  const pad = 14;
  const sil = makeCanvas((w + 2 * pad) * SCALE, (h + 2 * pad) * SCALE);
  const s = sil.getContext('2d');
  s.drawImage(img, pad * SCALE, pad * SCALE, w * SCALE, h * SCALE);
  s.globalCompositeOperation = 'source-in';
  s.fillStyle = colour;
  s.fillRect(0, 0, sil.width, sil.height);
  const glow = makeCanvas(sil.width, sil.height);
  const g = glow.getContext('2d');
  g.filter = `blur(${5 * SCALE}px)`;
  for (let i = 0; i < 4; i++) g.drawImage(sil, 0, 0);
  return { canvas: glow, pad };
}

// The card for these details: the layout with its height and lines.
const cardOf = (lay) => ({ ...lay.L, H: lay.H, lines: lay.lines });

// The parts that never move: gradient, soft glows, the divider.
function drawStaticBackground(L) {
  const c = makeCanvas(L.W * SCALE, L.H * SCALE);
  const ctx = c.getContext('2d');
  ctx.scale(SCALE, SCALE);
  const { paper, glows, dividerRgb } = L.brand;
  const base = ctx.createLinearGradient(0, 0, L.W, L.H);
  base.addColorStop(0, paper[0]);
  base.addColorStop(0.6, paper[1]);
  base.addColorStop(1, paper[2]);
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, L.W, L.H);
  ctx.save();
  ctx.filter = `blur(${L.W * 0.07 * SCALE}px)`;
  ctx.globalAlpha = 0.25;
  ctx.fillStyle = glows[0];
  ctx.beginPath();
  ctx.arc(L.W * 0.9, L.H * 0.05, L.W * 0.16, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 0.35;
  ctx.fillStyle = glows[1];
  ctx.beginPath();
  ctx.arc(L.W * 0.15, L.H * 1.05, L.W * 0.18, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  // No drawn background lines (the streaks glide along invisible paths);
  // laptop's divider, fading out at both ends.
  if (L.divider) {
    const g = ctx.createLinearGradient(0, 14, 0, L.H - 14);
    g.addColorStop(0, `rgba(${dividerRgb},0)`);
    g.addColorStop(0.5, `rgba(${dividerRgb},0.35)`);
    g.addColorStop(1, `rgba(${dividerRgb},0)`);
    ctx.fillStyle = g;
    ctx.fillRect(L.divider, 14, 1, L.H - 28);
  }
  return c;
}

// The streaks at time t (0..1), gliding along their lines.
function drawStreaks(ctx, L, t) {
  const pathLen = L.W * 1.15;
  const dashLen = L.W * 0.07;
  ctx.save();
  ctx.strokeStyle = L.brand.streak;
  ctx.lineCap = 'round';
  for (const [li, p] of L.streaks) {
    const phase = (t + p) % 1;
    const path = new Path2D(L.lines[li].d);
    ctx.setLineDash([dashLen, pathLen * 4]);
    ctx.lineDashOffset = dashLen - phase * (pathLen + dashLen);
    ctx.filter = `blur(${2.6 * SCALE}px)`;
    ctx.globalAlpha = 0.2;
    ctx.lineWidth = 5;
    ctx.stroke(path);
    ctx.filter = 'none';
    ctx.globalAlpha = 0.55;
    ctx.lineWidth = 1.6;
    ctx.stroke(path);
  }
  ctx.restore();
}

// Text with a soft glow shaped like the words, so the lines and streaks fade
// out as they reach it (as the HTML's text-shadow).
function haloText(ctx, text, x, y, halo) {
  const fill = ctx.fillStyle;
  ctx.save();
  ctx.shadowColor = halo;
  ctx.fillStyle = halo;
  for (const blur of [16, 12, 9, 6, 4, 2]) {
    ctx.shadowBlur = blur * SCALE;
    ctx.fillText(text, x, y);
  }
  ctx.restore();
  ctx.fillStyle = fill;
  ctx.fillText(text, x, y);
}

function setFont(ctx, size, letterSpacing = 0) {
  ctx.font = `bold ${size}px ${FONT}`;
  if ('letterSpacing' in ctx) ctx.letterSpacing = `${letterSpacing}px`;
}

// A logo with its glow.
function drawLogo(ctx, img, x, y, w, h, halo) {
  const glow = logoGlow(img, w, h, halo);
  ctx.drawImage(glow.canvas, x - glow.pad, y - glow.pad, w + 2 * glow.pad, h + 2 * glow.pad);
  ctx.drawImage(img, x, y, w, h);
}

// One round button at (cx, cy): LinkedIn or Facebook's letter, the Nano
// Utilities mark (the path from their logo, a 72-unit square) or the Nano
// Fibre logo, which has dark letters so its disc is white, with a fine ring
// to show on the light card.
function drawButton(ctx, button, cx, cy, brand, fibreLogo) {
  ctx.fillStyle = button.mark === 'fibre' ? '#ffffff' : brand.accent;
  ctx.beginPath();
  ctx.arc(cx, cy, SOCIAL_R, 0, Math.PI * 2);
  ctx.fill();
  if (button.mark === 'fibre') {
    ctx.strokeStyle = brand.accent;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(cx, cy, SOCIAL_R - 0.5, 0, Math.PI * 2);
    ctx.stroke();
    const w = 15;
    const h = (w * fibreLogo.naturalHeight) / fibreLogo.naturalWidth;
    ctx.drawImage(fibreLogo, cx - w / 2, cy - h / 2, w, h);
  } else if (button.mark === 'utilities') {
    const s = 0.3;
    ctx.save();
    ctx.translate(cx - 36 * s, cy - 36 * s);
    ctx.scale(s, s);
    ctx.strokeStyle = brand.accentInk;
    ctx.lineWidth = 8;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke(new Path2D('M16 52V32a10 10 0 0 1 20 0v8a10 10 0 0 0 20 0V20'));
    ctx.restore();
  } else {
    setFont(ctx, button.glyph === 'f' ? 15 : 12, 0);
    ctx.fillStyle = brand.accentInk;
    ctx.textAlign = 'center';
    ctx.fillText(button.glyph, cx, cy + (button.glyph === 'f' ? 1.5 : 0.5));
  }
}

// Everything in front of the background: the brand pieces, the details.
function drawForeground(lay, assets) {
  const { L, H } = lay;
  const { brand } = L;
  const { logo, fibreLogo, banners, icons } = assets[L.brandKey];
  const c = makeCanvas(L.W * SCALE, H * SCALE);
  const ctx = c.getContext('2d');
  ctx.scale(SCALE, SCALE);
  ctx.textBaseline = 'middle';

  // Brand pieces, from the column / footer top.
  ctx.save();
  ctx.translate(0, lay.brandY);
  const [lx, ly, lw, lh] = L.logo;
  drawLogo(ctx, logo, lx, ly, lw, lh, brand.halo);
  setFont(ctx, 10, brand.siteSpacing);
  ctx.fillStyle = brand.accent;
  ctx.textAlign = L.site.align;
  haloText(ctx, brand.site, L.site.x, L.site.y, brand.halo);
  brand.buttons.forEach((button, i) => drawButton(ctx, button, L.socials.xs[i], L.socials.y, brand, fibreLogo));
  ctx.restore();
  ctx.textAlign = 'left';

  const x = L.detailsX;
  if (lay.name) {
    setFont(ctx, lay.nameSize, -0.2);
    ctx.fillStyle = brand.ink;
    haloText(ctx, lay.name, x, lay.nameY, brand.halo);
  }
  if (lay.title) {
    setFont(ctx, lay.titleSize, 1.4);
    ctx.fillStyle = brand.accent;
    haloText(ctx, lay.title.toUpperCase(), x, lay.titleY, brand.halo);
  }
  ctx.fillStyle = brand.rule;
  ctx.fillRect(x, lay.ruleY, 36, 2);
  const rx = L.detailsX;
  for (const row of lay.rows) {
    ctx.drawImage(icons[row.icon], rx, row.y + (L.rowH - L.sizes.icon) / 2, L.sizes.icon, L.sizes.icon);
    setFont(ctx, row.size, 0);
    ctx.fillStyle = brand.text;
    haloText(ctx, row.text, rx + L.sizes.icon + 10, row.y + L.rowH / 2, brand.halo);
  }
  ctx.drawImage(banners[lay.banner][L.layoutKey], 0, lay.cardH, L.W, BANNER_H[lay.banner]);
  return c;
}

// With the carousel, the loop runs twice as long: it's the card's streaks
// twice over, and one step along per logo.
const framesOf = (lay) => (lay.banner === 'certs' ? CERT_LOGOS.length * (HOLD_FRAMES + SLIDE_FRAMES) : FRAMES);

const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);

// Where the carousel's row is in frame i (in slots; fractional while sliding).
function carouselOffset(i) {
  const per = HOLD_FRAMES + SLIDE_FRAMES;
  const step = Math.floor(i / per);
  const k = i % per;
  return k < HOLD_FRAMES ? step : step + ease((k - HOLD_FRAMES + 1) / (SLIDE_FRAMES + 1));
}

// The certification logos at frame i, sliding along the banner.
function drawCarousel(ctx, lay, assets, i) {
  const { L } = lay;
  const { slots, tile: [w, h] } = L.carousel;
  const tiles = assets[L.brandKey].tiles[L.layoutKey];
  const slotW = L.W / slots;
  const offset = carouselOffset(i);
  const first = Math.floor(offset);
  const top = lay.cardH + CAROUSEL_TOP;
  ctx.setTransform(SCALE, 0, 0, SCALE, 0, 0);
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, top, L.W, CAROUSEL_H);
  ctx.clip();
  for (let s = 0; s <= slots; s++) {
    const x = Math.round((s - (offset - first)) * slotW + (slotW - w) / 2);
    ctx.drawImage(tiles[(first + s) % tiles.length], x, top + (CAROUSEL_H - h) / 2, w, h);
  }
  ctx.restore();
}

// One whole frame: background, streaks at time t, foreground.
function drawFrame(ctx, L, staticBg, foreground, t) {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, L.W * SCALE, L.H * SCALE);
  ctx.drawImage(staticBg, 0, 0);
  ctx.setTransform(SCALE, 0, 0, SCALE, 0, 0);
  drawStreaks(ctx, L, t);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.drawImage(foreground, 0, 0);
}

// Frame i of the loop: the card, and the carousel if it has one.
function drawLoopFrame(ctx, lay, L, assets, staticBg, foreground, i) {
  drawFrame(ctx, L, staticBg, foreground, (i / FRAMES) % 1);
  if (lay.banner === 'certs') drawCarousel(ctx, lay, assets, i);
}

// Ordered (Bayer 4x4) dither, keyed to card coordinates so neighbouring
// slices line up: GIF's 256 colours band the soft lilac glows into visible
// steps; a fixed fine pattern smooths them without flicker between frames.
const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];
function dither(data, w, x0, y0) {
  for (let p = 0, n = data.length / 4; p < n; p++) {
    const x = x0 + (p % w);
    const y = y0 + Math.floor(p / w);
    const d = (BAYER[(y % 4) * 4 + (x % 4)] - 7.5) * 0.6;
    const o = p * 4;
    data[o] += d;
    data[o + 1] += d;
    data[o + 2] += d;
  }
  return data;
}

// RGBA frames -> GIF with the given palette (its last entry is the
// transparent "unchanged" colour); frames after the first keep only the
// pixels that changed, so it stays small.
function encodeGif(frames, w, h, palette) {
  const transparentIndex = palette.length - 1;
  const enc = GIFEncoder();
  let prev = null;
  for (const data of frames) {
    const idx = applyPalette(data, palette);
    if (prev) {
      const delta = new Uint8Array(idx.length);
      for (let p = 0; p < idx.length; p++) delta[p] = idx[p] === prev[p] ? transparentIndex : idx[p];
      enc.writeFrame(delta, w, h, { delay: FRAME_MS, transparent: true, transparentIndex, dispose: 1 });
    } else {
      enc.writeFrame(idx, w, h, { palette, delay: FRAME_MS, repeat: 0, dispose: 1 });
    }
    prev = idx;
  }
  enc.finish();
  return enc.bytes();
}

// The copied signature's images: every frame of the loop drawn at 2x, each
// slice cut out of it and encoded as its own animated GIF.
export async function renderSlices(lay, assets, slices) {
  const L = cardOf(lay);
  const staticBg = drawStaticBackground(L);
  const foreground = drawForeground(lay, assets);
  const canvas = makeCanvas(L.W * SCALE, L.H * SCALE);
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  const perSlice = slices.map(() => []);
  const sampleFrames = [];
  const frames = framesOf(lay);
  for (let i = 0; i < frames; i++) {
    drawLoopFrame(ctx, lay, L, assets, staticBg, foreground, i);
    if (i % 12 === 0) sampleFrames.push(dither(ctx.getImageData(0, 0, canvas.width, canvas.height).data, canvas.width, 0, 0));
    slices.forEach((s, k) => {
      const data = ctx.getImageData(s.x * SCALE, s.y * SCALE, s.w * SCALE, s.h * SCALE).data;
      perSlice[k].push(dither(data, s.w * SCALE, s.x * SCALE, s.y * SCALE));
    });
    // Let the page breathe between frames (the button shows progress).
    if (i % 8 === 7) await new Promise((r) => setTimeout(r, 0));
  }
  // One palette for every slice, from the whole card: per-slice palettes
  // round the soft gradients differently, and the joins would show.
  const sample = new Uint8Array(sampleFrames.reduce((n, f) => n + f.length, 0));
  let at = 0;
  for (const f of sampleFrames) {
    sample.set(f, at);
    at += f.length;
  }
  const palette = quantize(sample, 255);
  palette.push([0, 0, 0]);
  return slices.map((s, k) => ({ ...s, file: `${s.key}.gif`, bytes: encodeGif(perSlice[k], s.w * SCALE, s.h * SCALE, palette) }));
}

// The page's preview: the same card, played on a canvas (2x, shown at the
// canvas's CSS size). Returns a function that stops it.
export function animateSignature(canvas, lay, assets) {
  const L = cardOf(lay);
  const staticBg = drawStaticBackground(L);
  const foreground = drawForeground(lay, assets);
  canvas.width = L.W * SCALE;
  canvas.height = L.H * SCALE;
  const ctx = canvas.getContext('2d');
  const frames = framesOf(lay);
  let frame = 0;
  let timer = null;
  const tick = () => {
    drawLoopFrame(ctx, lay, L, assets, staticBg, foreground, frame);
    frame = (frame + 1) % frames;
    timer = setTimeout(tick, FRAME_MS);
  };
  tick();
  return () => clearTimeout(timer);
}
